import "@/App.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import Home from "@/pages/Home";
import SearchPage from "@/pages/Search";
import PlaceAd from "@/pages/PlaceAd";
import ItemView from "@/pages/ItemView";
import Dashboard from "@/pages/Dashboard";
import Admin from "@/pages/Admin";
import PaymentSuccess from "@/pages/PaymentSuccess";
import InfoPage from "@/pages/InfoPage";
import { CookieConsent } from "@/components/CookieConsent";

const FB_ERROR_MESSAGES = {
  email_required: "Facebook didn't share an email with us. Please register with your email instead.",
  cancelled: "Facebook sign-in was cancelled.",
  token: "We couldn't complete Facebook sign-in. Please try again.",
  network: "Network issue talking to Facebook. Please try again.",
};

function FacebookAuthNotice() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const err = params.get("fb_error");
    if (err) {
      toast.error(FB_ERROR_MESSAGES[err] || "Facebook sign-in failed.");
      params.delete("fb_error");
      const qs = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }
  }, []);
  return null;
}

// Handles the Emergent Google OAuth redirect (lands at /dashboard#session_id=...).
function AuthCallback() {
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const processed = useRef(false);
  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sid = new URLSearchParams(window.location.hash.slice(1)).get("session_id");
    (async () => {
      try {
        await api.post("/auth/google/session", {}, { headers: { "X-Session-ID": sid } });
        await refreshUser();
        toast.success("Signed in with Google!");
      } catch (e) {
        toast.error("Google sign-in failed. Please try again.");
      } finally {
        window.history.replaceState({}, "", "/dashboard");
        navigate("/dashboard", { replace: true });
      }
    })();
  }, [navigate, refreshUser]);
  return (
    <div className="min-h-screen flex items-center justify-center text-slate-600" data-testid="google-auth-callback">
      Signing you in…
    </div>
  );
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
      <FacebookAuthNotice />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/category/:slug" element={<SearchPage />} />
        <Route path="/place-ad" element={<PlaceAd />} />
        <Route path="/listing/:id" element={<ItemView />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/about" element={<InfoPage slug="about" />} />
        <Route path="/safety" element={<InfoPage slug="safety" />} />
        <Route path="/terms" element={<InfoPage slug="terms" />} />
        <Route path="/privacy" element={<InfoPage slug="privacy" />} />
        <Route path="/cookies" element={<InfoPage slug="cookies" />} />
      </Routes>
      <CookieConsent />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-center" richColors />
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
